import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  TreeSelect,
  message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  productBoxCategoryOptions,
  productBoxCreate,
  productBoxDelete,
  productBoxDetail,
  productBoxList,
  productBoxUpdate,
} from 'apis/product-box';
import usePagination from 'commons/hooks/usePagination';
import showTotal from 'components/show-total';
import { useCallback, useEffect, useState } from 'react';
import {
  PmsProductBoxCategoryOption,
  PmsProductBoxRecord,
  PmsProductBoxSaveDto,
} from 'types/pms-product-box';

type BoxFormValues = {
  name: string;
  nameZh: string;
  nameJa: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  enable: boolean;
  sort: number;
  productCategoryIds: number[];
};

const toTreeData = (options: PmsProductBoxCategoryOption[]): any[] =>
  options.map((option) => ({
    value: option.value,
    title: option.label,
    disabled: option.disabled,
    selectable: option.isLeaf && !option.disabled,
    disableCheckbox: !option.isLeaf || option.disabled,
    children: option.children?.length ? toTreeData(option.children) : undefined,
  }));

const ProductBoxPage = () => {
  const [keyword, setKeyword] = useState('');
  const [queryName, setQueryName] = useState('');
  const [categoryId, setCategoryId] = useState<number>();
  const [filterTree, setFilterTree] = useState<PmsProductBoxCategoryOption[]>(
    [],
  );
  const [formTree, setFormTree] = useState<PmsProductBoxCategoryOption[]>([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number>();
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<BoxFormValues>();
  const {
    loading,
    setLoading,
    dataSource,
    setDataSource,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
  } = usePagination<PmsProductBoxRecord>();

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productBoxList({
        pageNum,
        pageSize,
        name: queryName.trim() || undefined,
        productCategoryId: categoryId,
      });
      setDataSource(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [
    categoryId,
    queryName,
    pageNum,
    pageSize,
    setDataSource,
    setLoading,
    setTotal,
  ]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    productBoxCategoryOptions().then(setFilterTree);
  }, []);

  const openCreate = async () => {
    setEditingId(undefined);
    form.resetFields();
    form.setFieldsValue({
      enable: true,
      sort: 0,
      productCategoryIds: [],
    });
    setFormTree(await productBoxCategoryOptions());
    setOpen(true);
  };

  const openEdit = async (id: number) => {
    const [detail, options] = await Promise.all([
      productBoxDetail(id),
      productBoxCategoryOptions(id),
    ]);
    setEditingId(id);
    setFormTree(options);
    form.setFieldsValue({
      name: detail.name,
      nameZh: detail.nameZh,
      nameJa: detail.nameJa,
      lengthCm: detail.lengthCm,
      widthCm: detail.widthCm,
      heightCm: detail.heightCm,
      enable: detail.enable === 1,
      sort: detail.sort,
      productCategoryIds: detail.categories.map((item) => item.id),
    });
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    const payload: PmsProductBoxSaveDto = {
      name: values.name.trim(),
      nameZh: values.nameZh.trim(),
      nameJa: values.nameJa.trim(),
      lengthCm: values.lengthCm,
      widthCm: values.widthCm,
      heightCm: values.heightCm,
      enable: values.enable ? 1 : 0,
      sort: values.sort ?? 0,
      productCategoryIds: values.productCategoryIds ?? [],
    };
    setSaving(true);
    try {
      if (editingId) {
        await productBoxUpdate(editingId, payload);
        message.success('已保存');
      } else {
        await productBoxCreate(payload);
        message.success('已创建');
      }
      setOpen(false);
      productBoxCategoryOptions().then(setFilterTree);
      loadList();
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<PmsProductBoxRecord> = [
    { title: '中文名', dataIndex: 'nameZh', width: 160 },
    { title: '英文名', dataIndex: 'name', width: 160 },
    { title: '日文名', dataIndex: 'nameJa', width: 160 },
    {
      title: '外盒尺寸 (cm)',
      width: 180,
      render: (_, row) => `${row.lengthCm} × ${row.widthCm} × ${row.heightCm}`,
    },
    {
      title: '体积重 (kg)',
      dataIndex: 'volumetricWeightKg',
      width: 120,
    },
    {
      title: '关联分类',
      render: (_, row) =>
        row.categories.length ? (
          <Space size={[4, 4]} wrap>
            {row.categories.map((item) => (
              <Tag key={item.id}>{item.displayName}</Tag>
            ))}
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: '关联商品数',
      dataIndex: 'linkedProductCount',
      width: 120,
    },
    {
      title: '启用',
      width: 80,
      render: (_, row) => (row.enable === 1 ? '是' : '否'),
    },
    {
      title: '操作',
      width: 140,
      render: (_, row) => (
        <Space>
          <Button type="link" onClick={() => openEdit(row.id)}>
            编辑
          </Button>
          <Popconfirm
            title="删除这个 Box？"
            onConfirm={async () => {
              await productBoxDelete(row.id);
              message.success('已删除');
              productBoxCategoryOptions().then(setFilterTree);
              loadList();
            }}
          >
            <Button type="link" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Box尺寸"
      extra={
        <Button type="primary" onClick={openCreate}>
          新建
        </Button>
      }
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          allowClear
          placeholder="名称"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          style={{ width: 200 }}
        />
        <TreeSelect
          allowClear
          showSearch
          treeNodeFilterProp="title"
          placeholder="关联分类"
          style={{ width: 280 }}
          value={categoryId}
          treeData={toTreeData(filterTree)}
          onChange={(value) => {
            setCategoryId(value);
            setPageNum(1);
          }}
        />
        <Button
          type="primary"
          onClick={() => {
            setQueryName(keyword);
            if (pageNum === 1 && queryName === keyword) loadList();
            else setPageNum(1);
          }}
        >
          查询
        </Button>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showTotal,
          onChange: (nextPage, nextSize) => {
            setPageNum(nextPage);
            setPageSize(nextSize);
          },
        }}
      />
      <Drawer
        title={editingId ? '编辑 Box' : '新建 Box'}
        width={520}
        open={open}
        onClose={() => setOpen(false)}
        extra={
          <Button type="primary" loading={saving} onClick={submit}>
            保存
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="中文名"
            name="nameZh"
            rules={[
              { required: true, whitespace: true, message: '请填写中文名' },
            ]}
          >
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item
            label="英文名"
            name="name"
            rules={[
              { required: true, whitespace: true, message: '请填写英文名' },
            ]}
          >
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item
            label="日文名"
            name="nameJa"
            rules={[
              { required: true, whitespace: true, message: '请填写日文名' },
            ]}
          >
            <Input maxLength={100} />
          </Form.Item>
          <Space>
            <Form.Item
              label="长 (cm)"
              name="lengthCm"
              rules={[{ required: true, message: '请填写长度' }]}
            >
              <InputNumber min={0.1} step={0.1} precision={1} />
            </Form.Item>
            <Form.Item
              label="宽 (cm)"
              name="widthCm"
              rules={[{ required: true, message: '请填写宽度' }]}
            >
              <InputNumber min={0.1} step={0.1} precision={1} />
            </Form.Item>
            <Form.Item
              label="高 (cm)"
              name="heightCm"
              rules={[{ required: true, message: '请填写高度' }]}
            >
              <InputNumber min={0.1} step={0.1} precision={1} />
            </Form.Item>
          </Space>
          <Form.Item label="排序" name="sort">
            <InputNumber />
          </Form.Item>
          <Form.Item label="启用" name="enable" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="叶子分类" name="productCategoryIds">
            <TreeSelect
              treeCheckable
              showSearch
              treeNodeFilterProp="title"
              showCheckedStrategy={TreeSelect.SHOW_CHILD}
              placeholder="选择叶子分类"
              treeData={toTreeData(formTree)}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
};

export default ProductBoxPage;
